/**
 * @fileoverview Virtual Race Authorization & Form Submission
 * @requires virtual-race-utility.js
 */

(function() {
    'use strict';

    if (typeof window.VirtualRaceUtils === 'undefined') {
        console.error('VirtualRaceUtils not loaded');
        return;
    }

    const { CONFIG, SecurityUtils, StorageManager, BaseAPIClient, UIHelpers, DateHelpers, DOMHelpers } = window.VirtualRaceUtils;

    class RaceAPIClient extends BaseAPIClient {

        async submitRace(formData) {
            return await this._executeWithRetry(async () => {
                const apiDomain = window.globalSpace?.apiDomain || '';
                const response = await this._fetchWithTimeout(
                    `/apps/proxy/virtual-race/create`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(formData)
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();

                if (data.statusCode !== 200) {
                    throw new Error('Invalid response status');
                }

                return data;
            }, 'raceSubmission');
        }
    }

    // ============================================================================
    // FORM VALIDATOR
    // ============================================================================

    class FormValidator {

        static validateForm(form) {
            let isValid = true;

            // Validate required fields
            form.querySelectorAll('[data-required]').forEach(input => {
                if (!this.validateField(input, form)) {
                    isValid = false;
                }
            });
            if (!this.validateRadioGroups(form)) {
                isValid = false;
            }
            if (!this.validateTosCheckbox(form)) {
                isValid = false;
            }

            return isValid;
        }

        static validateTosCheckbox(form) {
            const wrapper = form.closest('.vr-entertime-form') || form;
            const checkboxes = wrapper.querySelectorAll('[data-tos-checkbox]');
            let isValid = true;

            checkboxes.forEach(checkbox => {
                const container = checkbox.closest('.form-check');
                if (container && container.closest('.d-none')) return;

                const alert = container?.querySelector('[data-tos-alert]');
                if (!checkbox.checked) {
                    if (alert) alert.classList.remove('d-none');
                    isValid = false;
                } else {
                    if (alert) alert.classList.add('d-none');
                }
            });

            return isValid;
        }

        static validateField(input, form, options = {}) {
            const value = input.value;
            const parentNode = input.closest('.input-wrap');
            const alert = input.parentElement?.querySelector('[data-alert]') ||
                         parentNode?.querySelector('[data-alert]');
            if (parentNode?.classList.contains('d-none') || input.closest('.d-none')) {
                return true;
            }
            if (input.tagName === 'SELECT' && input.id === 'dwfrm_racestats_team') {
                return this.validateTeamSelect(input, alert);
            }
            if (input.type === 'email') {
                return this.validateEmailField(input, alert, options.live === true);
            }
            if (!value) {
                UIHelpers.showAlert(alert);
                return false;
            }
            switch (input.id) {
                case 'dwfrm_racestats_totalDistance':
                    return this.validateDistanceField(input, alert);
                case 'dwfrm_racestats_racedate':
                    return this.validateDateField(input, alert);
                case 'dwfrm_racestats_racetime__hh':
                case 'dwfrm_racestats_racetime__mm':
                case 'dwfrm_racestats_racetime__ss':
                    return this.validateTimeFields(form);
                default:
                    UIHelpers.hideAlert(alert);
                    return true;
            }
        }

        /**
         * Email is editable in both the manual and the connected (OAuth) flow,
         * so the address is checked for shape before it can be submitted.
         * In live mode (typing) only emptiness is flagged, so a half-typed
         * address does not turn the field red mid-keystroke.
         */
        static validateEmailField(input, alert, live) {
            const value = input.value.trim();

            if (!value) {
                if (alert) alert.textContent = 'This field is required.';
                UIHelpers.showAlert(alert);
                return false;
            }

            if (!live && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
                if (alert) alert.textContent = 'Please enter a valid email address.';
                UIHelpers.showAlert(alert);
                return false;
            }

            UIHelpers.hideAlert(alert);
            return true;
        }

        static validateTeamSelect(input, alert) {
            const teamWrapper = input.closest('[data-teamWrapper]');
            const isVisible = teamWrapper && !teamWrapper.classList.contains('d-none');

            if (isVisible) {
                if (!input.value || input.value === 'N/A') {
                    UIHelpers.showAlert(alert);
                    input.classList.add('is-invalid');
                    return false;
                }
                UIHelpers.hideAlert(alert);
                input.classList.remove('is-invalid');
            }

            return true;
        }

        static validateDistanceField(input, alert) {
            const distance = parseFloat(input.value);

            if (!SecurityUtils.validateDistance(distance)) {
                UIHelpers.showAlert(alert);
                return false;
            }

            UIHelpers.hideAlert(alert);
            return true;
        }

        static validateDateField(input, alert) {
            if (SecurityUtils.validateDate(input.value)) {
                UIHelpers.hideAlert(alert);
                return true;
            }

            UIHelpers.showAlert(alert);
            return false;
        }

        static validateTimeFields(form) {
            const hhInput = form.querySelector('#dwfrm_racestats_racetime__hh');
            const mmInput = form.querySelector('#dwfrm_racestats_racetime__mm');
            const ssInput = form.querySelector('#dwfrm_racestats_racetime__ss');
            const inputWrap = hhInput?.closest('.input-wrap');

            if (!inputWrap) return true;

            const hhAlert = inputWrap.querySelector('.hh-alert');
            const mmAlert = inputWrap.querySelector('.mm-alert');
            const ssAlert = inputWrap.querySelector('.ss-alert');

            // Hide all alerts first
            UIHelpers.hideAlert(hhAlert);
            UIHelpers.hideAlert(mmAlert);
            UIHelpers.hideAlert(ssAlert);

            const hhVal = hhInput?.value.trim() || '';
            const mmVal = mmInput?.value.trim() || '';
            const ssVal = ssInput?.value.trim() || '';

            let isValid = true;
            if (!hhVal) {
                UIHelpers.showAlert(hhAlert);
                isValid = false;
            }
            if (!mmVal) {
                UIHelpers.showAlert(mmAlert);
                isValid = false;
            }
            if (!ssVal) {
                UIHelpers.showAlert(ssAlert);
                isValid = false;
            }
            if (hhVal && !SecurityUtils.validateTimeComponent(hhVal, 'hours')) {
                UIHelpers.showAlert(hhAlert);
                isValid = false;
            }
            if (mmVal && !SecurityUtils.validateTimeComponent(mmVal, 'minutes')) {
                UIHelpers.showAlert(mmAlert);
                isValid = false;
            }
            if (ssVal && !SecurityUtils.validateTimeComponent(ssVal, 'seconds')) {
                UIHelpers.showAlert(ssAlert);
                isValid = false;
            }

            return isValid;
        }

        static validateRadioGroups(form) {
            const requiredRadios = form.querySelectorAll('input[type="radio"][data-required]');
            const radioGroupNames = new Set();

            requiredRadios.forEach(input => {
                radioGroupNames.add(input.name);
            });

            let isValid = true;

            radioGroupNames.forEach(name => {
                const checkedRadio = form.querySelector(`input[type="radio"][name="${name}"]:checked`);
                const firstRadio = form.querySelector(`input[type="radio"][name="${name}"]`);
                const parentNode = firstRadio?.closest('.input-wrap');

                if (parentNode && !parentNode.classList.contains('d-none')) {
                    const alert = parentNode.querySelector('[data-alert]');
                    if (checkedRadio) {
                        UIHelpers.hideAlert(alert);
                    } else {
                        UIHelpers.showAlert(alert);
                        isValid = false;
                    }
                }
            });

            return isValid;
        }
    }

    // ============================================================================
    // FORM DATA BUILDER
    // ============================================================================

    class FormDataBuilder {

        static build(form) {
            const userData = StorageManager.getUserData();
            const activityData = StorageManager.getSelectedActivity();
            const selectedProduct = StorageManager.getSelectedProduct();
            const activityType = StorageManager.getActivityType();
            const name = this.extractName(form, userData, activityType);
            const email = this.extractEmail(form, userData);
            const date = this.extractDate(form, activityData);
            const distance = this.extractDistance(form);
            const distanceUnit = this.extractDistanceUnit(form);
            const raceTime = this.extractRaceTime(form);
            const activity = this.extractActivity(form, activityData);
            const runningSpace = this.extractRadioValue(form, 'dwfrm_racestats_environment');
            const ranAlone = this.extractRadioBoolean(form, 'dwfrm_racestats_alone');
            const personalBest = this.extractRadioBoolean(form, 'dwfrm_racestats_bestTime');
            const zipCode = this.extractZipCode(form);
            const DOB = this.extractDOB(form);
            const trackingDevice = this.extractValue(form, '#dwfrm_racestats_timeTracker');
            const gender = this.extractRadioValue(form, 'dwfrm_racestats_gender');
            const note = this.extractNote(form);
            const team = this.extractTeam(form);
            const consent = this.extractConsent(form);

            return {
                name,
                email,
                date,
                distance,
                distanceUnit,
                raceTime,
                activity,
                runningSpace,
                ranAlone,
                personalBest,
                zipCode,
                DOB,
                trackingDevice,
                gender,
                note,
                product_id: String(selectedProduct || ''),
                team,
                consent
            };
        }

        static extractConsent(form) {
            const consentCheckbox = form.querySelector('[data-tos-checkbox]');
            return consentCheckbox ? consentCheckbox.checked : false;
        }

        /**
         * Returns the first field the shopper can actually see and edit.
         * The connected (OAuth) block and the manual block both carry a name
         * and an email input, so preferring a visible one keeps us from reading
         * the copy that is hidden for the current flow.
         */
        static getVisibleInput(form, selectors) {
            let fallback = null;

            for (const selector of selectors) {
                const input = form.querySelector(selector);
                if (!input) continue;
                if (!input.closest('.d-none')) return input;
                if (!fallback) fallback = input;
            }

            return fallback;
        }

        static extractName(form, userData, activityType) {
            const nameInput = this.getVisibleInput(form, [
                '[data-connected-name]',
                '#dwfrm_racestats_name'
            ]);
            const typedName = nameInput ? nameInput.value.trim() : '';

            if (typedName) {
                return SecurityUtils.sanitizeText(typedName);
            }

            return userData?.name ? SecurityUtils.sanitizeText(userData.name) : '';
        }

        static extractEmail(form, userData) {
            const emailInput = this.getVisibleInput(form, [
                '[data-connected-email]',
                '#dwfrm_racestats_email'
            ]);
            const typedEmail = emailInput ? emailInput.value.trim() : '';

            if (typedEmail) {
                return SecurityUtils.sanitizeEmail(typedEmail);
            }

            return userData?.email ? SecurityUtils.sanitizeEmail(userData.email) : '';
        }

        static extractDate(form, activityData) {
            const dateInput = form.querySelector('#dwfrm_racestats_racedate');
            const dateValue = dateInput?.value || activityData?.date || '';

            if (dateValue) {
                return new Date(dateValue).toISOString();
            }

            return '';
        }

        static extractDistance(form) {
            const distanceInput = form.querySelector('#dwfrm_racestats_totalDistance');
            return distanceInput ? parseFloat(distanceInput.value) || 0 : 0;
        }

        static extractDistanceUnit(form) {
            const unitSelect = form.querySelector('#dwfrm_racestats_distanceUnit');
            return unitSelect ? SecurityUtils.sanitizeText(unitSelect.value) : 'mile';
        }

        static extractRaceTime(form) {
            const hhInput = form.querySelector('#dwfrm_racestats_racetime__hh');
            const mmInput = form.querySelector('#dwfrm_racestats_racetime__mm');
            const ssInput = form.querySelector('#dwfrm_racestats_racetime__ss');

            const hh = String(hhInput?.value || '0').padStart(2, '0');
            const mm = String(mmInput?.value || '0').padStart(2, '0');
            const ss = String(ssInput?.value || '0').padStart(2, '0');

            return `${hh}:${mm}:${ss}`;
        }

        static extractActivity(form, activityData) {
            const activitySelect = form.querySelector('#dwfrm_racestats_activity');
            const value = activitySelect?.value || activityData?.name || '';
            return SecurityUtils.sanitizeText(value);
        }

        static extractRadioValue(form, name) {
            const radio = form.querySelector(`[name="${name}"]:checked`);
            return radio ? SecurityUtils.sanitizeText(radio.value) : '';
        }

        static extractRadioBoolean(form, name) {
            const radio = form.querySelector(`[name="${name}"]:checked`);
            return radio ? radio.value.toLowerCase() === 'true' : false;
        }

        static extractZipCode(form) {
            const zipInput = form.querySelector('#dwfrm_racestats_where');
            return zipInput ? SecurityUtils.sanitizeZipCode(zipInput.value) : '';
        }

        static extractDOB(form) {
            const dobInput = form.querySelector('#dwfrm_racestats_birthday');
            const dobValue = dobInput?.value || '';

            if (dobValue) {
                return new Date(dobValue).toISOString();
            }

            return '';
        }

        static extractValue(form, selector) {
            const element = form.querySelector(selector);
            return element ? SecurityUtils.sanitizeText(element.value) : '';
        }

        static extractNote(form) {
            const textarea = form.querySelector('textarea');
            return textarea ? SecurityUtils.sanitizeText(textarea.value) : '';
        }

        static extractTeam(form) {
            const teamInput = form.querySelector('#dwfrm_racestats_team');
            return teamInput ? SecurityUtils.sanitizeText(teamInput.value) : null;
        }
    }

    // ============================================================================
    // KLAVIYO TRACKER
    // ============================================================================

    const KLAVIYO_API_BASE = 'https://a.klaviyo.com/client';
    // 2025-01-15+ is required for the explicit `subscriptions` consent below.
    const KLAVIYO_REVISION = '2026-10-15';
    const KLAVIYO_RACE_METRIC = 'Submitted Virtual Race Time';

    /**
     * Read before submitting: showSuccess() clears the stored activity type.
     */
    function collectSubmitContext(form) {
        const isConnected = new URLSearchParams(window.location.search).get('logged') === 'true';
        const optInCheckbox = form.querySelector('[data-newsletter-optin]');

        return {
            listId: (form.dataset.klaviyoListId || '').trim(),
            newsletterOptIn: Boolean(optInCheckbox?.checked),
            raceName: document.querySelector('[data-product-title]')?.textContent.trim() || '',
            entryMethod: (isConnected && StorageManager.getActivityType()) || 'manual',
            brand: document.body.dataset.brand || ''
        };
    }

    /**
     * Sends saved race entries to Klaviyo through `window.klaviyo`, the object
     * loaded by the Klaviyo Shopify app embed. Nothing runs if it is missing.
     *
     * `profileExists` comes from the recap endpoint (true, false or null for
     * unknown) and decides whether an un-opted-in entry is recorded:
     *  - Opted in: identify, subscribe to the newsletter list, track.
     *  - Not opted in, profile exists: identify, track (attaches to it).
     *  - Not opted in, no profile: nothing, so no new profile is created.
     *  - Not opted in, unknown: track only if Klaviyo already recognizes the
     *    browser, identifying the typed email first so it wins over the cookie.
     *
     * Fire-and-forget: a Klaviyo failure never affects the race submission.
     */
    class KlaviyoRaceTracker {

        static async send(formData, context, profileExists) {
            const klaviyo = window.klaviyo;
            if (!klaviyo || typeof klaviyo.account !== 'function' || !formData.email) return;

            try {
                if (!(await this.shouldRecord(klaviyo, context, profileExists))) return;

                const accountId = await klaviyo.account();
                await this.recordRace(klaviyo, accountId, formData, context);
            } catch (error) {
                console.warn('Klaviyo sync failed:', error?.message);
            }
        }

        static async shouldRecord(klaviyo, context, profileExists) {
            if (context.newsletterOptIn) return true;
            if (profileExists === true) return true;
            if (profileExists === false) return false;
            return Boolean(await klaviyo.isIdentified());
        }

        static async recordRace(klaviyo, accountId, formData, context) {
            await klaviyo.identify(this.buildIdentity(formData));

            // A failed subscribe must not stop the race event from being tracked.
            if (context.newsletterOptIn && context.listId) {
                try {
                    await this.postSubscription(accountId, formData, context);
                } catch (error) {
                    console.warn('Klaviyo subscribe failed:', error?.message);
                }
            }

            await klaviyo.track(KLAVIYO_RACE_METRIC, this.buildRaceProperties(formData, context));
        }

        static buildIdentity(formData) {
            const [firstName, ...rest] = (formData.name || '').split(/\s+/);
            const identity = { email: formData.email };

            if (firstName) identity.first_name = firstName;
            if (rest.length) identity.last_name = rest.join(' ');

            return identity;
        }

        static buildRaceProperties(formData, context) {
            return {
                race_name: context.raceName,
                race_product_id: formData.product_id,
                race_date: formData.date,
                race_time: formData.raceTime,
                distance: formData.distance,
                distance_unit: formData.distanceUnit,
                activity: formData.activity,
                running_space: formData.runningSpace,
                ran_alone: formData.ranAlone,
                personal_best: formData.personalBest,
                gender: formData.gender,
                zip_code: formData.zipCode,
                birthday: formData.DOB,
                tracking_device: formData.trackingDevice,
                team: formData.team,
                note: formData.note,
                has_comment: Boolean(formData.note),
                newsletter_opt_in: context.newsletterOptIn,
                terms_consent: formData.consent,
                entry_method: context.entryMethod,
                brand: context.brand,
                page_url: window.location.href
            };
        }

        static postSubscription(accountId, formData, context) {
            return this.post('subscriptions', accountId, {
                data: {
                    type: 'subscription',
                    attributes: {
                        custom_source: 'Virtual Race Time Entry',
                        profile: {
                            data: {
                                type: 'profile',
                                attributes: {
                                    email: formData.email,
                                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                                }
                            }
                        }
                    },
                    relationships: {
                        list: { data: { type: 'list', id: context.listId } }
                    }
                }
            });
        }

        static async post(endpoint, accountId, body) {
            const response = await fetch(
                `${KLAVIYO_API_BASE}/${endpoint}/?company_id=${encodeURIComponent(accountId)}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/vnd.api+json',
                        'revision': KLAVIYO_REVISION
                    },
                    body: JSON.stringify(body),
                    keepalive: true
                }
            );

            if (!response.ok) {
                throw new Error(`Klaviyo ${endpoint} HTTP ${response.status}`);
            }
        }
    }

    // ============================================================================
    // RECAP NOTIFY (Salesforce)
    // ============================================================================

    const RECAP_NOTIFY_URL = 'https://chalktalksports.my.salesforce-sites.com/services/apexrest/vr/recap-notify';
    const RECAP_NOTIFY_TIMEOUT_MS = 4000;

    /**
     * Calls the Salesforce recap endpoint, which posts comments to Slack and
     * reports whether a Klaviyo profile exists for the email. The Slack URL and
     * Klaviyo private key live only in Salesforce.
     *
     * Optional by design: runs after the race is saved, never throws, and any
     * failure (timeout, non-200, ok:false, bad JSON) resolves to null, which
     * the Klaviyo tracker treats as unknown.
     *
     * Sent as text/plain so the browser skips the CORS preflight. Salesforce
     * rejects unknown top-level keys, quoted booleans and over-length strings,
     * so values are typed and trimmed here.
     */
    class RecapNotifyClient {

        static async getProfileExists(formData, context) {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), RECAP_NOTIFY_TIMEOUT_MS);

            try {
                const response = await fetch(RECAP_NOTIFY_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'text/plain' },
                    body: JSON.stringify(this.buildPayload(formData, context)),
                    signal: controller.signal
                });
                if (!response.ok) return null;

                const data = await response.json();
                return data?.ok === true && typeof data.profileExists === 'boolean'
                    ? data.profileExists
                    : null;
            } catch (error) {
                console.warn('Recap notify failed:', error?.name);
                return null;
            } finally {
                clearTimeout(timer);
            }
        }

        static buildPayload(formData, context) {
            const text = (value, max = 100) => {
                const trimmed = String(value ?? '').trim();
                return trimmed ? trimmed.substring(0, max) : null;
            };
            const bool = (value) => (typeof value === 'boolean' ? value : null);
            const day = (iso) => (iso ? String(iso).slice(0, 10) : null);
            const distance = Number(formData.distance);

            return {
                email: formData.email,
                name: text(formData.name),
                newsletterOptIn: context.newsletterOptIn,
                comment: text(formData.note, 1000),
                race: {
                    name: text(context.raceName),
                    productId: /^\d+$/.test(String(formData.product_id)) ? String(formData.product_id) : null,
                    date: day(formData.date),
                    time: text(formData.raceTime),
                    distance: Number.isFinite(distance) && distance >= 0 ? distance : null,
                    distanceUnit: text(formData.distanceUnit),
                    activity: text(formData.activity),
                    runningSpace: text(formData.runningSpace),
                    ranAlone: bool(formData.ranAlone),
                    personalBest: bool(formData.personalBest),
                    gender: text(formData.gender),
                    birthday: day(formData.DOB),
                    zip: text(formData.zipCode),
                    team: text(formData.team),
                    trackingDevice: text(formData.trackingDevice),
                    entryMethod: text(context.entryMethod),
                    termsConsent: bool(formData.consent),
                    brand: text(context.brand)
                }
            };
        }
    }

    // ============================================================================
    // AUTHORIZATION MANAGER
    // ============================================================================

    class AuthorizationManager {
        constructor() {

            this._apiClient = new RaceAPIClient();

            this._pollAttempts = 0;

            this._pollIntervalId = null;

            this.init();
        }

        init() {
            this.initFlatpickr();
            this.initConnection();
        }

        initFlatpickr() {
            document.addEventListener('DOMContentLoaded', () => {
                const birthdayInput = document.querySelector('#dwfrm_racestats_birthday');
                if (!birthdayInput || !window.flatpickr) return;

                const maxDate = new Date();
                maxDate.setFullYear(maxDate.getFullYear() - CONFIG.MIN_AGE);

                window.flatpickr(birthdayInput, {
                    dateFormat: 'm/d/Y',
                    maxDate: maxDate,
                    disableMobile: true,
                    allowInput: false,
                    onChange: (selectedDates) => {
                        this.validateBirthdayOnChange(birthdayInput, selectedDates[0]);
                    }
                });
            });
        }

        validateBirthdayOnChange(input, selectedDate) {
            const alertEl = input.closest('.flex-1')?.querySelector('[data-alert]');
            if (!alertEl) return;

            if (SecurityUtils.validateAge(selectedDate.toISOString())) {
                UIHelpers.hideAlert(alertEl);
                input.setCustomValidity('');
            } else {
                UIHelpers.showAlert(alertEl);
                input.setCustomValidity('Must be at least 13 years old');
            }
        }

        initConnection() {
            this._pollAttempts = 0;

            this._pollIntervalId = setInterval(() => {
                const connections = document.querySelectorAll('[data-connect]');

                if (connections.length > 0) {
                    clearInterval(this._pollIntervalId);
                    this.setupFormHandlers();
                }

                this._pollAttempts++;
                if (this._pollAttempts >= CONFIG.MAX_POLL_ATTEMPTS) {
                    clearInterval(this._pollIntervalId);
                }
            }, CONFIG.POLL_INTERVAL);
        }

        setupFormHandlers() {
            this.checkConnected();
            this.setupRaceForm();
            this.setupSubmitButtons();
            this.setupInputValidation();
        }

        setupRaceForm() {
            const raceForm = document.querySelector('[data-raceform]');
            if (!raceForm) return;

            raceForm.addEventListener('submit', (event) => {
                event.preventDefault();
            });
        }

        setupSubmitButtons() {
            const submitBtn = document.querySelector('[data-submitRacetime]');

            if (submitBtn) {
                submitBtn.addEventListener('click', (event) => {
                    this.handleSubmit(event);
                });
            }
        }

        setupInputValidation() {
            const form = document.querySelector('.vr-entertime-form');
            if (!form) return;

            const inputs = form.querySelectorAll('input');
            inputs.forEach(input => {
                input.addEventListener('input', () => {
                    // Real-time validation on change
                    FormValidator.validateField(input, form, { live: true });
                });
                input.addEventListener('blur', () => {
                    FormValidator.validateField(input, form);
                });
            });
        }

        async handleSubmit(event) {
            event.preventDefault();

            const form = event.target.closest('.vr-entertime-form form');
            if (!form) return;

            const isValid = FormValidator.validateForm(form);

            if (!isValid) return;

            const formData = FormDataBuilder.build(form);
            const submitContext = collectSubmitContext(form);

            try {
                await this.submitRace(formData);
                // Only saved entries reach Salesforce (Slack) and Klaviyo.
                this.syncAfterSubmit(formData, submitContext);
            } catch (error) {
                console.error('Submit error:', error.name);
                alert('Unable to submit race. Please try again later.');
            }
        }

        /**
         * Runs after the race is saved and the success screen is shown. Never
         * awaited by the submit flow, so nothing here can block or fail it.
         */
        async syncAfterSubmit(formData, context) {
            const profileExists = await RecapNotifyClient.getProfileExists(formData, context);
            await KlaviyoRaceTracker.send(formData, context, profileExists);
        }

        async submitRace(formData) {
            const data = await this._apiClient.submitRace(formData);

            if (data.statusCode === 200) {
                this.showSuccess(formData);
            }
        }

        showSuccess(formData) {
            const raceForm = document.querySelector('[data-raceform]');
            const successSection = document.querySelector('[data-successrace]');
            const summaryEl = document.querySelector('[data-displayracesummary]');

            if (raceForm) DOMHelpers.hide(raceForm);
            if (successSection) DOMHelpers.show(successSection);

            if (summaryEl) {
                const nameP = document.createElement('p');
                nameP.className = 'h4';
                nameP.textContent = `Congratulations `;
                const strong = document.createElement('strong');
                strong.textContent = formData.name;
                nameP.appendChild(strong);

                const timeP = document.createElement('p');
                timeP.className = 'h5';
                timeP.textContent = 'You completed the race in ';
                const span = document.createElement('span');
                span.className = 'text-primary';
                span.textContent = formData.raceTime;
                timeP.appendChild(span);

                summaryEl.innerHTML = '';
                summaryEl.appendChild(nameP);
                summaryEl.appendChild(timeP);
            }

            this.clearConnectionData();
        }

        clearConnectionData() {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('logged');
            urlParams.delete('inconnected');
            
            const newUrl = urlParams.toString() 
                ? `${window.location.pathname}?${urlParams.toString()}`
                : window.location.pathname;
            
            window.history.replaceState({}, '', newUrl);

            try {
                const storagePrefix = CONFIG.STORAGE_KEY_PREFIX || 'vr_';
                localStorage.removeItem(`${storagePrefix}userData`);
                localStorage.removeItem(`${storagePrefix}selectedActivity`);
                localStorage.removeItem(`${storagePrefix}activityType`);
            } catch (error) {
                console.error('Failed to clear storage:', error.name);
            }
        }

        checkConnected() {
            const urlParams = new URLSearchParams(window.location.search);
            const logged = urlParams.get('logged');
            const isConnected = logged === 'true';

            if (isConnected) {
                this.handleConnectedUser();
            } else {
                this.handleDisconnectedUser();
            }
        }

        handleConnectedUser() {
            const activityType = StorageManager.getActivityType();
            const userData = StorageManager.getUserData();
            const activityData = StorageManager.getSelectedActivity();
            const formInputs = document.querySelectorAll('[data-forminputs]');
            formInputs.forEach(el => DOMHelpers.hide(el));
            const outsideRadio = document.querySelector('[name="dwfrm_racestats_environment"][value="outside"]');
            if (outsideRadio) outsideRadio.checked = true;
            const activityTypes = ['strava', 'fitbit', 'garmin'];
            if (activityTypes.includes(activityType)) {
                this.cleanupForOAuth(activityType);
                if(document.querySelector('[data-connectedUserdetails]')){
                    document.querySelector('[data-connectedUserdetails]')?.classList.remove('d-none');
                }
            }
            if (activityType) {
                this.populateFormData(activityType, userData, activityData);
            }

            // Show success text
            this.showActivitySuccess(activityData);

            // after
        }

        handleDisconnectedUser() {
            const formInputs = document.querySelectorAll('[data-forminputs]');
            formInputs.forEach(el => DOMHelpers.show(el));
            const connectedDetails = document.querySelector('[data-connectedUserdetails]');
            connectedDetails?.remove();
            
        }

        cleanupForOAuth(activityType) {
            if(activityType === 'garmin' || activityType === 'fitbit' || activityType === 'strava') {
                const removeEmailForGarmin = document.querySelector('[data-remove-email-for-garmin]');
                if (removeEmailForGarmin) {
                    removeEmailForGarmin.remove();
                }
            }
            const teamWrapper = document.querySelector('[data-teamWrapper]');   
            if(teamWrapper) {
                teamWrapper.remove();
            }
            const teamSelect = document.querySelector('[data-team-select]');
            if(teamSelect) {
                teamSelect.classList.add('loading');
                setTimeout(() => {
                    teamSelect.options[0].selected = true;
                    teamSelect.classList.remove('loading');
                }, 5000);
            }
        }

        populateFormData(activityType, userData, activityData) {
            const form = document.querySelector('.vr-entertime-form');
            if (!form) return;
            if (userData?.gender) {
                this.setGender(form, userData.gender);
            }
            this.prefillConnectedIdentity(form, userData);
            if (activityData) {
                switch (activityType) {
                    case 'garmin':
                        this.populateGarminData(form, activityData);
                        break;
                    case 'strava':
                        this.populateStravaData(form, activityData, userData);
                        break;
                    case 'fitbit':
                        this.populateFitbitData(form, activityData, userData);
                        break;
                }
            }
        }

        /**
         * Seeds the connected-user name/email with whatever the tracker returned.
         * This runs once during init, before the shopper can type, so the tracker
         * value wins over the Liquid-rendered customer default. Anything the
         * tracker does not supply keeps the customer default. Both inputs stay
         * editable and whatever they hold at submit time is what gets sent.
         */
        prefillConnectedIdentity(form, userData) {
            const nameInput = form.querySelector('[data-connected-name]');
            if (nameInput && userData?.name) {
                nameInput.value = String(userData.name);
            }

            const emailInput = form.querySelector('[data-connected-email]');
            if (emailInput && userData?.email) {
                emailInput.value = String(userData.email);
            }
        }

        setGender(form, gender) {
            const genderMap = {
                'MALE': 'M',
                'FEMALE': 'F'
            };

            const genderValue = genderMap[gender] || gender;
            const genderRadio = form.querySelector(`[name="dwfrm_racestats_gender"][value="${genderValue}"]`);

            if (genderRadio) {
                genderRadio.checked = true;
            }
        }

        populateGarminData(form, activityData) {
            const teamInput = form.querySelector('#dwfrm_racestats_team');
            if (teamInput) {
                teamInput.removeAttribute('data-required');
            }

            this.setFormValue(form, '#dwfrm_racestats_totalDistance', activityData.distance);

            if (activityData.duration) {
                this.setTimeValues(form, activityData.duration);
            }

            if (activityData.date) {
                const formattedDate = DateHelpers.toYMD(activityData.date);
                this.setFormValue(form, '#dwfrm_racestats_racedate', formattedDate);
            }

            const removeForGarmin = document.querySelector('[data-remove-for-garmin]');
            if (removeForGarmin) {
                removeForGarmin.remove();
            }

            const unitSelect = form.querySelector('#dwfrm_racestats_distanceUnit');
            if (unitSelect && activityData.distanceUnit === 'mile') {
                unitSelect.value = 'mile';
            }
        }

        populateStravaData(form, activityData, userData) {
            if (activityData.date) {
                const formattedDate = DateHelpers.toYMD(activityData.date);
                this.setFormValue(form, '#dwfrm_racestats_racedate', formattedDate);
            }
            
            if (activityData.duration) {
                this.setTimeValues(form, activityData.duration);
            }

            this.setFormValue(form, '#dwfrm_racestats_totalDistance', activityData.distance || '0');
            this.setFormValue(form, '#dwfrm_racestats_distanceUnit', 'mile');

            const mainInputWrapper = form.querySelector('[data-forminputs]');
            if(mainInputWrapper) {
                const teamWrapper = mainInputWrapper.querySelector('[data-teamWrapper]');
                if(teamWrapper) {
                    teamWrapper.remove();
                }
                setTimeout(() => {
                const teamSeletor_2nd = form.querySelector('[data-team-select]');
                    if(teamSeletor_2nd) {
                        teamSeletor_2nd.options[0].selected = true;
                    }
                }, 1000);
            }
        }

        populateFitbitData(form, activityData, userData) {
            if (activityData.date) {
                const formattedDate = DateHelpers.toYMD(activityData.date);
                this.setFormValue(form, '#dwfrm_racestats_racedate', formattedDate);
            }

            if (activityData.duration) {
                this.setTimeValues(form, activityData.duration);
            }

            this.setFormValue(form, '#dwfrm_racestats_totalDistance', activityData.distance);
            this.setFormValue(form, '#dwfrm_racestats_distanceUnit', 'mile');

            const mainInputWrapper = form.querySelector('[data-forminputs]');
            
            if(mainInputWrapper) {
                const teamWrapper = mainInputWrapper.querySelector('[data-teamWrapper]');
                if(teamWrapper) {
                    teamWrapper.remove();
                }
                setTimeout(() => {
                const teamSeletor_2nd = form.querySelector('[data-team-select]');
                console.log('teamSeletor_2nd', teamSeletor_2nd);
                    if(teamSeletor_2nd) {
                        teamSeletor_2nd.options[0].selected = true;
                    }
                }, 1000);
            }
        }

        setFormValue(form, selector, value) {
            const input = form.querySelector(selector);
            if (input && value !== undefined && value !== null) {
                input.value = String(value);
            }
        }

        setTimeValues(form, duration) {
            const parts = duration.split(':');
            const hh = parts[0] || '';
            const mm = parts[1] || '';
            const ss = parts[2] || '';

            this.setFormValue(form, '#dwfrm_racestats_racetime__hh', hh);
            this.setFormValue(form, '#dwfrm_racestats_racetime__mm', mm);
            this.setFormValue(form, '#dwfrm_racestats_racetime__ss', ss);
        }

        showActivitySuccess(activityData) {
            if (!activityData) return;

            const successText = document.querySelector('[data-successText]');
            if (!successText) return;

            const distance = activityData.distance || '0';
            const unit = distance === '1' ? 'mile' : 'miles';
            const name = SecurityUtils.sanitizeText(activityData.name || 'activity');
            const duration = SecurityUtils.sanitizeText(activityData.duration || '0:00:00');

            const message = `Congratulations! You completed your ${name} ${distance} ${unit} in ${duration}`;

            successText.textContent = message;
            DOMHelpers.show(successText);
        }

        destroy() {
            if (this._pollIntervalId) {
                clearInterval(this._pollIntervalId);
            }

            if (this._apiClient) {
                this._apiClient.cancelPendingRequests();
            }
        }
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================
    if (typeof window !== 'undefined') {
        new AuthorizationManager();
    }

})();
